import { appStorage } from '../../lib/safeStorage';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Check, CheckCircle2, Loader2,
  Save, Music, AlertCircle, Send, Pencil, Lock, Phone, Mail,
  Eye, CalendarCheck, MailCheck, Plus, Trash2, GripVertical, Search,
  Link2, ExternalLink,
} from 'lucide-react';
import { isFieldVisible, computeCompletion, getPlanningSchema, applyCascadeClear, getAllSchemaFields } from '../../config/planningSchemas';
import { computeSectionScore, getStatusMeta } from '../../lib/planningScoring';
import SchemaMirroredAnswers from './SchemaMirroredAnswers';
import SongSearchModal from './SongSearchModal';
import AdminSongSections from './AdminSongSections';

import { MoneyInput } from '../ui/MoneyInput';
import { formatPhoneNumber, getPhoneDigits } from '../../utils/phoneFormatter';

const API = process.env.REACT_APP_BACKEND_URL;
// Resolve the schema's own `mapTo` declarations into the shape the submit
// endpoint expects. planningSchemas.js declares `mapTo: {target, field}` on
// ~40 fields per event type, but nothing ever executed it -- so the venue
// phone, room name, floor, elevator, setting and arrival times a client
// typed never reached the Logistics tile the crew actually read. They were
// never lost (the brief renders raw answers), just never fanned out.
//
// Only VISIBLE fields are mapped, so a field hidden by a showIf can't push a
// stale value downstream. Objects/arrays (repeating groups) stay in
// `answers` -- the operational collections hold scalars.
export function buildMappedPayload(schema, answers) {
  const out = {};
  for (const f of getAllSchemaFields(schema)) {
    const target = f.mapTo && f.mapTo.target;
    const name = f.mapTo && f.mapTo.field;
    if (!target || !name) continue;
    if (!isFieldVisible(f, answers)) continue;
    const v = answers[f.id];
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object') continue;
    if (!out[target]) out[target] = {};
    out[target][name] = v;
  }
  // Legacy fallback: the song_picker renders two textareas (mustPlaySongs /
  // doNotPlaySongs) that are no longer schema-declared. Pass them through to
  // music so a client on an un-resolvable legacy event never loses what they
  // typed. On the normal path these keys are absent and nothing happens.
  for (const k of ['mustPlaySongs', 'doNotPlaySongs']) {
    const v = answers[k];
    if (typeof v === 'string' && v.trim()) {
      if (!out.music) out.music = {};
      out.music[k] = v;
    }
  }
  return out;
}

// ── Field Renderers ────────────────────────────────────────────────────

function TextField({ field, value, onChange, error }) {
  return (
    <input
      type={field.type === 'phone' ? 'tel' : field.type === 'email' ? 'email' : 'text'}
      value={value || ''}
      onChange={e => onChange(field.id, e.target.value)}
      placeholder={field.placeholder || ''}
      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
      data-testid={`pf-field-${field.id}`}
    />
  );
}

function TextareaField({ field, value, onChange }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(field.id, e.target.value)}
      placeholder={field.placeholder || ''}
      rows={field.rows || 3}
      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition resize-none"
      data-testid={`pf-field-${field.id}`}
    />
  );
}

function NumberField({ field, value, onChange }) {
  return (
    <MoneyInput
      allowDecimals={false}
      // Generic schema number — guest counts, hours, headcounts. Not money,
      // so no symbol. A currency field on this form declares its own.
      showSymbol={false}
      value={value ?? ''}
      onChange={e => onChange(field.id, e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
      placeholder={field.placeholder || '0'}
      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
      data-testid={`pf-field-${field.id}`}
    />
  );
}

function DateField({ field, value, onChange }) {
  return (
    <input
      type="date"
      value={value || ''}
      onChange={e => onChange(field.id, e.target.value)}
      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
      data-testid={`pf-field-${field.id}`}
    />
  );
}

function TimeField({ field, value, onChange }) {
  return (
    <input
      type="time"
      value={value || ''}
      onChange={e => onChange(field.id, e.target.value)}
      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
      data-testid={`pf-field-${field.id}`}
    />
  );
}

function YesNoField({ field, value, onChange }) {
  return (
    <div className="flex gap-3" data-testid={`pf-field-${field.id}`}>
      {['yes', 'no'].map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(field.id, opt)}
          className={`flex-1 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 ${
            value === opt
              ? opt === 'yes'
                ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#E7C75E]'
                : 'bg-white/[0.06] border border-white/15 text-white/60'
              : 'bg-white/[0.02] border border-white/[0.06] text-white/30 hover:text-white/50 hover:border-white/10'
          }`}
          data-testid={`pf-field-${field.id}-${opt}`}
        >
          {opt === 'yes' ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );
}

function DropdownField({ field, value, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(field.id, e.target.value)}
      className="w-full bg-[#0c0e1a] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition appearance-none"
      data-testid={`pf-field-${field.id}`}
    >
      <option value="">Select...</option>
      {(field.options || []).map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function SongField({ field, value, onChange }) {
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <div className="relative">
      <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]/25 pointer-events-none" />
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(field.id, e.target.value)}
        placeholder={field.placeholder || 'Song title — Artist'}
        className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg pl-9 pr-10 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
        data-testid={`pf-field-${field.id}`}
      />
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="no-hover-lift absolute right-1.5 top-2 w-7 h-7 rounded-md flex items-center justify-center text-[#D4AF37]/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/15 transition-colors"
        aria-label="Search for a song"
        data-testid={`pf-song-search-${field.id}`}
      >
        <Search className="w-3.5 h-3.5" />
      </button>
      <SongSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={(val) => onChange(field.id, val)}
        fieldLabel={field.label}
      />
    </div>
  );
}

function UrlField({ field, value, onChange }) {
  const v = (value || '').trim();
  const looksUrl = /^https?:\/\//i.test(v);
  return (
    <div className="relative">
      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]/25 pointer-events-none" />
      <input
        type="url"
        value={value || ''}
        onChange={e => onChange(field.id, e.target.value)}
        placeholder={field.placeholder || 'https://...'}
        className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition"
        data-testid={`pf-field-${field.id}`}
      />
      {v && looksUrl && (
        <a href={v} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-[#1DB954] hover:underline" data-testid={`pf-url-open-${field.id}`}>
          <ExternalLink className="w-3 h-3" /> Open link
        </a>
      )}
      {v && !looksUrl && (
        <p className="mt-1.5 text-[11px] text-amber-400/70">Add the full link (starting with https://)</p>
      )}
    </div>
  );
}

// Single song picker — opens the SAME branded /songs/<token> page the admin
// shares, so songs have one source of truth (Must Play / Do Not Play / Play
// If Possible). Legacy fallback: if the song link can't be resolved for this
// event, render the original two textareas so the client is never stuck.
function SongPickerField({ songRequestLink, answers, onChange }) {
  if (songRequestLink) {
    return (
      <div
        className="rounded-xl p-4"
        style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)' }}
        data-testid="pf-song-picker"
      >
        <p className="text-[13px] text-white/60 leading-relaxed mb-3">
          Build your song lists on one page — sort each song into
          <span className="text-[#E6C86A]"> Must Play</span>,
          <span className="text-[#F19B9B]"> Do Not Play</span>, or
          <span className="text-[#8FB8F2]"> Play If Possible</span>. Search with
          30-second previews or paste a Spotify playlist.
        </p>
        <a
          href={songRequestLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold text-[#0B0F1A] transition-transform duration-200 active:scale-[0.99] hover:-translate-y-px"
          style={{ background: 'linear-gradient(135deg, #E6C86A, #C5A028)' }}
          data-testid="pf-song-picker-open"
        >
          <Music className="w-4 h-4" />
          Open your song requests
        </a>
      </div>
    );
  }
  // Legacy fallback — the original two textareas (mapped in buildMappedPayload).
  return (
    <div className="space-y-3" data-testid="pf-song-picker-fallback">
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-[#D4AF37]/70 font-medium mb-1.5">Must-Play Songs</label>
        <textarea
          value={answers.mustPlaySongs || ''}
          onChange={(e) => onChange('mustPlaySongs', e.target.value)}
          placeholder="List any songs that must be played..."
          rows={3}
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition resize-none"
          data-testid="pf-field-mustPlaySongs"
        />
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-widest text-[#D4AF37]/70 font-medium mb-1.5">Do-Not-Play Songs</label>
        <textarea
          value={answers.doNotPlaySongs || ''}
          onChange={(e) => onChange('doNotPlaySongs', e.target.value)}
          placeholder="Any songs or genres to avoid..."
          rows={3}
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white/75 placeholder:text-white/18 focus:outline-none focus:border-[#D4AF37]/30 focus:ring-1 focus:ring-[#D4AF37]/20 transition resize-none"
          data-testid="pf-field-doNotPlaySongs"
        />
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, error }) {
  switch (field.type) {
    case 'textarea': return <TextareaField field={field} value={value} onChange={onChange} />;
    case 'number': return <NumberField field={field} value={value} onChange={onChange} />;
    case 'date': return <DateField field={field} value={value} onChange={onChange} />;
    case 'time': return <TimeField field={field} value={value} onChange={onChange} />;
    case 'yes_no': return <YesNoField field={field} value={value} onChange={onChange} />;
    case 'dropdown': return <DropdownField field={field} value={value} onChange={onChange} />;
    case 'song': return <SongField field={field} value={value} onChange={onChange} />;
    case 'url': return <UrlField field={field} value={value} onChange={onChange} />;
    default: return <TextField field={field} value={value} onChange={onChange} error={error} />;
  }
}


// ── Repeater Field (dynamic list of sub-field rows) ────────────────────
//
// Renders an expandable list of {itemFields}-shaped rows. Use for
// "+ Add sibling / + Add vendor / + Add custom candle lighter" flows
// where the count is unknown in advance. Stored as an array of
// objects in answers[field.id]. Starts with `defaultItems` blank rows
// (or any saved rows on hydration) and grows on "+ Add" click.
//
// Rows are rendered in the same paired 2-column grid so `Name ↔ Song`
// sits side-by-side like the rest of the Introductions step.

function RepeaterField({ field, value, onChange }) {
  const normalize = (raw) => {
    if (Array.isArray(raw)) return raw;
    const seed = Math.max(1, field.defaultItems || 1);
    return Array.from({ length: seed }, () => ({}));
  };
  const items = normalize(value);
  const maxItems = field.maxItems ?? 20;
  const minItems = field.minItems ?? 0;

  // ── Unified pointer-based drag (works on both mouse AND touch) ──────
  // We drive the whole reorder off pointer events rather than HTML5 drag
  // because the native drag API is mouse-only on iOS/Android browsers.
  // Drag is initiated from the GripVertical handle only (so typing in the
  // inputs doesn't start a drag), and on move we use elementFromPoint to
  // figure out which row the pointer is hovering so we can preview the
  // insertion line. On release we splice the items array.
  const listRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const commit = (next) => onChange(field.id, next);
  const setRow = (idx, sub, v) => {
    const next = items.map((r, i) => (i === idx ? { ...r, [sub]: v } : r));
    commit(next);
  };
  const addRow = () => {
    if (items.length >= maxItems) return;
    commit([...items, {}]);
  };
  const removeRow = (idx) => {
    if (items.length <= minItems) return;
    commit(items.filter((_, i) => i !== idx));
  };
  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
  };

  // Resolve the row index under the pointer at (x, y) using elementFromPoint
  // — works for both mouse and touch because pointer events are unified.
  const rowIdxAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    const row = el.closest?.('[data-row-idx]');
    if (!row || !listRef.current?.contains(row)) return null;
    const n = Number(row.getAttribute('data-row-idx'));
    return Number.isFinite(n) ? n : null;
  };

  const onHandlePointerDown = (e, idx) => {
    if (items.length <= 1) return;
    // Capture the pointer on the handle so subsequent move events come
    // to us even when the pointer leaves the handle's bounds.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    setDragIdx(idx);
    setOverIdx(idx);
  };
  const onHandlePointerMove = (e) => {
    if (dragIdx == null) return;
    // Prevent touch-scroll from fighting the drag on mobile.
    if (e.pointerType === 'touch') e.preventDefault?.();
    const target = rowIdxAtPoint(e.clientX, e.clientY);
    if (target != null && target !== overIdx) setOverIdx(target);
  };
  const onHandlePointerUp = () => {
    if (dragIdx != null && overIdx != null) reorder(dragIdx, overIdx);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div ref={listRef} className="space-y-3" data-testid={`pf-repeater-${field.id}`}>
      {items.map((row, idx) => {
        const isDragging = dragIdx === idx;
        const isDropTarget = overIdx === idx && dragIdx !== null && dragIdx !== idx;
        return (
          <div
            key={idx}
            data-row-idx={idx}
            className="relative grid grid-cols-[22px_1fr_1fr] gap-x-3 gap-y-3 items-start rounded-lg p-3 -m-3 transition-all"
            style={{
              background: isDragging ? 'rgba(212,175,55,0.04)' : 'transparent',
              opacity: isDragging ? 0.5 : 1,
              boxShadow: isDropTarget ? 'inset 0 2px 0 rgba(212,175,55,0.6)' : 'none',
            }}
            data-testid={`pf-repeater-row-${field.id}-${idx}`}
          >
            {/* Drag handle column — pointer-based, mouse + touch unified */}
            <div
              onPointerDown={(e) => onHandlePointerDown(e, idx)}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              className={`flex items-center justify-center pt-7 ${items.length > 1 ? 'cursor-grab active:cursor-grabbing text-white/20 hover:text-[#D4AF37]/70' : 'opacity-0'} transition-colors select-none`}
              style={{ touchAction: items.length > 1 ? 'none' : 'auto' }}
              title={items.length > 1 ? 'Drag to reorder' : ''}
              data-testid={`pf-repeater-handle-${field.id}-${idx}`}
            >
              <GripVertical className="w-3.5 h-3.5" />
            </div>
            {field.itemFields.map((sub) => {
              const subValue = row?.[sub.id] ?? '';
              const subField = { ...sub, placeholder: sub.placeholder };
              return (
                <div key={sub.id}>
                  <label className="block text-[11px] uppercase tracking-widest text-[#D4AF37]/70 font-medium mb-1.5">
                    {sub.label}
                  </label>
                  <FieldRenderer
                    field={subField}
                    value={subValue}
                    onChange={(_, v) => setRow(idx, sub.id, v)}
                  />
                </div>
              );
            })}
            {items.length > minItems && (
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="absolute -top-1 right-0 p-1.5 rounded-full text-white/25 hover:text-red-400/80 hover:bg-red-500/10 transition-all"
                title={`Remove ${field.itemLabel || 'row'}`}
                data-testid={`pf-repeater-remove-${field.id}-${idx}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        disabled={items.length >= maxItems}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold tracking-wide transition-all duration-200 disabled:opacity-40 hover:-translate-y-px"
        style={{
          background: 'rgba(212,175,55,0.06)',
          border: '1px solid rgba(212,175,55,0.18)',
          color: '#E6C86A',
        }}
        data-testid={`pf-repeater-add-${field.id}`}
      >
        <Plus className="w-3 h-3" />
        {field.addLabel || `+ Add ${field.itemLabel || 'row'}`}
      </button>
    </div>
  );
}


// ── Progress Bar ───────────────────────────────────────────────────────

function StepProgress({ steps, currentIdx, answers, onJump, disabled }) {
  const activeRef = useRef(null);
  const total = steps.length;
  const currentName = steps[currentIdx]?.title || '';

  // Keep the ACTIVE section visible in the horizontal rail at EVERY width (S31).
  // Instant, never smooth — this app sets scroll-behavior:smooth globally and
  // smooth programmatic scrolls are dropped in production. block:'nearest'
  // scrolls ONLY the rail horizontally and never moves the page vertically.
  // Instant scrolling is not animated, so it is safe under prefers-reduced-motion.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = activeRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
    } catch {
      el.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  }, [currentIdx]);

  return (
    <div className="flex flex-col gap-2" data-testid="pf-step-progress-wrap">
      {/* Which section am I on, how many are left, and a thin bar. Shown at
          all widths (S31) so the "where am I" line appears on desktop too. */}
      <div className="flex flex-col gap-1.5" data-testid="pf-step-mobile-status">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-semibold text-white/80 truncate min-w-0" data-testid="pf-step-mobile-name">
            {currentName}
          </span>
          <span className="text-[11px] font-medium text-white/40 shrink-0" data-testid="pf-step-mobile-count">
            Section {currentIdx + 1} of {total}
          </span>
        </div>
        <div className="h-1 rounded-full bg-white/10 overflow-hidden" data-testid="pf-step-mobile-bar">
          <div
            className="h-full rounded-full transition-all duration-300 motion-reduce:transition-none"
            style={{
              width: `${total > 0 ? ((currentIdx + 1) / total) * 100 : 0}%`,
              background: 'linear-gradient(90deg, #B88B1A 0%, #D4AF37 60%, #F2D989 100%)',
            }}
          />
        </div>
      </div>
      <div className="flex flex-1 items-start gap-2 sm:gap-3 text-center overflow-x-auto pb-2 min-w-0 snap-x"
           style={{ WebkitOverflowScrolling: 'touch' }}
           data-testid="pf-step-progress">
        {steps.map((step, idx) => {
          const isCurrent = idx === currentIdx;
          const sectionScore = computeSectionScore(step, answers || {});
          // Only actual completion turns a step green — merely scrolling
          // past a step no longer auto-marks it as done. This keeps the
          // state semantically honest (silver = not yet complete).
          const done = sectionScore.status === 'complete';
          const attention = sectionScore.status === 'needs_attention';

          // Color priority unchanged: current (gold) > done (green) >
          // attention > neutral. S31: attention no longer paints the bar/label
          // amber (it read as "current" on navy) — it uses the NEUTRAL bar and
          // label and signals itself with a small amber dot before the label.
          const barStyle = isCurrent
            ? {
                background: 'linear-gradient(90deg, #B88B1A 0%, #D4AF37 50%, #F2D989 100%)',
                boxShadow: '0 0 16px rgba(212,175,55,0.28)',
              }
            : done
            ? { background: 'linear-gradient(90deg, rgba(34,197,94,0.85), rgba(74,222,128,0.85))' }
            : { background: 'rgba(255,255,255,0.12)' };

          const labelColor = isCurrent
            ? '#D4AF37'
            : done
            ? 'rgba(74,222,128,0.9)'
            : 'rgba(255,255,255,0.45)';

          return (
            <button
              key={step.id}
              ref={idx === currentIdx ? activeRef : null}
              type="button"
              onClick={() => !disabled && !isCurrent && onJump && onJump(idx)}
              disabled={disabled}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={`Jump to ${step.title}`}
              className={`group flex-shrink-0 w-[140px] sm:w-[148px] snap-start space-y-2 rounded-md p-1 transition-all ${
                disabled || isCurrent
                  ? 'cursor-default'
                  : 'cursor-pointer hover:bg-white/[0.03] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 active:scale-[0.98]'
              }`}
              data-testid={`pf-step-${idx}`}
            >
              <div className="h-1 rounded-full transition-all duration-500" style={barStyle} />
              <span className="flex items-center justify-center gap-1">
                {attention && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: '#F59E0B' }}
                    title="Needs attention"
                    aria-label="Needs attention"
                    data-testid={`pf-step-attention-dot-${idx}`}
                  />
                )}
                <span
                  className="block text-[10px] sm:text-[11px] leading-snug font-medium transition-colors break-words"
                  style={{ color: labelColor }}
                >
                  {step.title}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export default function PlanningFormRenderer({
  schema,
  token,
  initialAnswers,
  readOnly,
  previewOnly,
  onSubmitted,
  eventDate,
  companyIndustry,
  booking,
  locked = false,
  lockContact = null,
  adminSave = null,
  initialStepId = null,
  songRequestLink = '',
  songAdminEventId = '',
}) {
  // Seed empty prefill-eligible fields from the client's booking. Any field
  // that declares `prefillFrom: 'eventDate' | 'startTime' | 'endTime' | 'venue'`
  // and has no saved answer picks up the value from booking[...] on first
  // render. The set of IDs we actually seeded is tracked so the field can
  // render a small "Filled in from your booking — update if it changed."
  // caption, which disappears the moment the client edits that field.
  const [answers, setAnswers] = useState(() => {
    const initial = { ...(initialAnswers || {}) };
    if (booking && schema?.steps) {
      for (const step of schema.steps) {
        for (const f of step.fields || []) {
          if (!f.prefillFrom) continue;
          if (initial[f.id] !== undefined && initial[f.id] !== '' && initial[f.id] !== null) continue;
          const seed = booking[f.prefillFrom];
          if (seed === undefined || seed === null || seed === '') continue;
          initial[f.id] = seed;
        }
      }
    }
    return initial;
  });
  const [prefilledFields, setPrefilledFields] = useState(() => {
    const s = new Set();
    if (booking && schema?.steps) {
      for (const step of schema.steps) {
        for (const f of step.fields || []) {
          if (!f.prefillFrom) continue;
          const hadSaved = initialAnswers && initialAnswers[f.id] !== undefined && initialAnswers[f.id] !== '' && initialAnswers[f.id] !== null;
          const seed = booking[f.prefillFrom];
          if (!hadSaved && seed !== undefined && seed !== null && seed !== '') {
            s.add(f.id);
          }
        }
      }
    }
    return s;
  });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitToast, setSubmitToast] = useState(null); // {type:'success'|'error', text}
  const saveTimer = useRef(null);
  const toastTimer = useRef(null);
  // True from the moment a field changes until a save actually succeeds.
  const unsavedRef = useRef(false);
  // Field ids changed since the last SUCCESSFUL save (S33). doSave sends only
  // these; the server merges per field so two devices never erase each other.
  const dirtyRef = useRef(new Set());
  // Always-current mirror of `answers` so doSave's success branch can tell
  // whether a field changed AGAIN during its in-flight request.
  const answersRef = useRef(answers);

  // Lock state is resolved by the server (tenant days rule OR admin override)
  // and passed in as `locked` — one source of truth, no client-side date math.
  const isLockedByDate = !!locked;

  // Check if already submitted
  useEffect(() => {
    if (initialAnswers?._submitted) setSubmitted(true);
  }, [initialAnswers]);

  const rawSteps = schema?.steps || [];

  // Industry-aware filter — drop fields explicitly audienced to an industry
  // that doesn't match the signed-in company. Fields without an `audience`
  // array are always shown (backward-compat default). If filtering a step
  // leaves it fieldless, the step is removed entirely so the progress bar
  // doesn't show a phantom empty step.
  const industryKey = (() => {
    const m = { dj_company: 'dj', event_planner: 'planner' };
    return m[companyIndustry] || null;
  })();
  const steps = useMemo(() => {
    if (!industryKey) return rawSteps;
    return rawSteps
      .map((s) => {
        const filteredFields = (s.fields || []).filter((f) => !f.audience || f.audience.includes(industryKey));
        if (filteredFields.length === 0) return null;
        return { ...s, fields: filteredFields };
      })
      .filter(Boolean);
  }, [rawSteps, industryKey]);

  const currentStep = steps[currentIdx];
  const isLastStep = currentIdx === steps.length - 1;
  const completion = computeCompletion({ ...schema, steps }, answers);

  // Restore the section the client left off on. The current step is saved to
  // the server as they navigate; on a refresh we reopen it instead of jumping
  // back to Section 1. Runs once, only when a saved step is present and valid,
  // so it never fights the client's own navigation afterwards.
  const stepRestoredRef = useRef(false);
  useEffect(() => {
    if (stepRestoredRef.current || !steps.length) return;
    stepRestoredRef.current = true;
    if (initialStepId) {
      const i = steps.findIndex((s) => s.id === initialStepId);
      if (i > 0) setCurrentIdx(i);
    }
  }, [steps, initialStepId]);

  // ── Autosave (debounced) ───────────────────────────────────────────
  const doSave = useCallback(async (ans, stepId) => {
    // Preview (admin "Preview as client" / ?preview=1) never writes.
    if (previewOnly) return;
    // Admin "Fill in for client" saves through the admin endpoint (adminSave)
    // and must work even after the client submitted — so the submitted guard
    // only applies to the client's own draft path.
    if (readOnly || (submitted && !editing && !adminSave)) return;
    // ── Admin "Fill in for client" — save the FULL answers object through
    // the existing admin planning save (same planning_submissions record the
    // client and staff read). A failed save NEVER clears the dirty set or the
    // on-screen answers, and only a confirmed success stamps "Saved".
    if (adminSave) {
      const source = ans || answersRef.current || {};
      setSaving(true);
      try {
        await adminSave(source);
        setLastSaved(new Date());
        setSaveError(null);
        unsavedRef.current = false;
        dirtyRef.current.clear();
      } catch (e) {
        setSaveError(e.message || 'Not saved — please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }
    // Send ONLY what changed since the last successful save. Snapshot the
    // dirty ids now; the server merges per field and never replaces the whole
    // answers object. Navigation saves with nothing dirty still send an empty
    // `changes` so `currentStep` persists cheaply.
    const snapshot = Array.from(dirtyRef.current);
    const source = ans || answersRef.current || {};
    const changes = {};
    const removed = [];
    for (const id of snapshot) {
      const v = source[id];
      if (v === undefined) removed.push(id);
      else changes[id] = v;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/leads/client-view/${token}/planning-v2`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          changes,
          removed,
          currentStep: stepId,
          eventType: schema.eventType,
        }),
      });
      // The old code awaited fetch WITHOUT checking res.ok, so a 400 or 500
      // still stamped "Saved". The backend rejects draft saves once the form
      // has been submitted -- that rejection was completely invisible here.
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `Save failed (${res.status})`);
      }
      const data = await res.json().catch(() => null);
      setLastSaved(new Date());
      setSaveError(null);
      unsavedRef.current = false;
      // Clear from the dirty set ONLY the ids we just sent whose value has not
      // changed again since the snapshot. Anything re-edited during the request
      // stays dirty and is sent next time.
      const latest = answersRef.current || {};
      for (const id of snapshot) {
        const sentVal = Object.prototype.hasOwnProperty.call(changes, id) ? changes[id] : undefined;
        if (latest[id] === sentVal) dirtyRef.current.delete(id);
      }
      // Merge the server's answers into local state for every field that is
      // NOT locally dirty — this is how the other person's answers appear.
      const serverAnswers = data && data.answers;
      if (serverAnswers && typeof serverAnswers === 'object') {
        setAnswers(prev => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(serverAnswers)) {
            if (!dirtyRef.current.has(k)) next[k] = v;
          }
          return next;
        });
      }
    } catch (e) {
      // NOT silent any more, and the dirty set is left untouched so nothing is
      // lost. A client filling this in on venue wifi used to watch a reassuring
      // "Saved" while nothing reached the server.
      setSaveError(e.message || 'Not saved - check your connection.');
    } finally {
      setSaving(false);
    }
    // `editing` MUST be here. It is read in the guard above, and without it
    // this callback kept a stale `editing === false` after the client hit
    // Edit on a submitted form -- so every autosave returned early and their
    // corrections were silently discarded unless they pressed Submit again.
  }, [token, schema, readOnly, submitted, editing, previewOnly, adminSave]);

  const scheduleAutosave = useCallback((ans, stepId) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(ans, stepId), 2000);
  }, [doSave]);

  // Cleanup
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);
  // Keep the answers mirror current for doSave's in-flight comparison + merge.
  useEffect(() => { answersRef.current = answers; }, [answers]);
  // Warn before leaving with a debounced save still pending. The autosave
  // timer is 2s, so the last thing typed -- usually the final field of a
  // long form -- was lost on a tab close with no warning at all.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      // In preview nothing is ever saved, so never warn about "unsaved" work.
      if (previewOnly) return undefined;
      if (!unsavedRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [previewOnly]);

  const handleChange = (fieldId, value) => {
    // Find the field definition to check if it's a yes_no toggle
    const fieldDef = (currentStep?.fields || []).find(f => f.id === fieldId);
    let next;
    if (fieldDef?.type === 'yes_no' && value === 'no') {
      // Apply dependent cascade: clear child fields that become hidden
      next = applyCascadeClear(schema, answers, fieldId, value);
    } else {
      next = { ...answers, [fieldId]: value };
    }
    setAnswers(next);
    // Mark dirty through this ONE setter (S33): the edited field plus any
    // cascade-cleared dependents. In preview nothing is marked, sent or merged.
    if (!previewOnly) {
      dirtyRef.current.add(fieldId);
      for (const k of new Set([...Object.keys(answers), ...Object.keys(next)])) {
        if (answers[k] !== next[k]) dirtyRef.current.add(k);
      }
    }
    if (prefilledFields.has(fieldId)) {
      setPrefilledFields(prev => {
        const s = new Set(prev);
        s.delete(fieldId);
        return s;
      });
    }
    setErrors(prev => ({ ...prev, [fieldId]: undefined }));
    unsavedRef.current = true;
    scheduleAutosave(next, currentStep?.id);
  };

  // ── Validation ─────────────────────────────────────────────────────
  const validateStep = (step) => {
    const errs = {};
    (step.fields || []).forEach(f => {
      if (!isFieldVisible(f, answers)) return;
      if (f.required) {
        const v = answers[f.id];
        if (v === undefined || v === null || v === '' || (typeof v === 'string' && !v.trim())) {
          errs[f.id] = 'Required';
        }
      }
    });
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep(currentStep);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    // Save on step transition
    doSave(answers, steps[currentIdx + 1]?.id || currentStep.id);
    setCurrentIdx(i => Math.min(i + 1, steps.length - 1));
    setErrors({});
  };

  const handleBack = () => {
    setCurrentIdx(i => Math.max(i - 1, 0));
    setErrors({});
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    // Preview (admin "Preview as client" / ?preview=1) never submits.
    if (previewOnly) return;
    // Validate current step
    const errs = validateStep(currentStep);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Flush any pending dirty fields first so the stored draft is current,
    // then submit (the server overlays submitted answers onto stored ones).
    await doSave(answersRef.current, currentStep?.id);

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/client-view/${token}/planning-v2/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          eventType: schema.eventType,
          mapped: buildMappedPayload(schema, answers),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submit failed');
      }

      const data = await res.json();
      setSubmitted(true);
      // Fire an inline toast so the client sees immediate confirmation
      // before the "Submitted" card fully takes over the viewport.
      setSubmitToast({ type: 'success', text: 'Planning details submitted — thank you!' });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setSubmitToast(null), 4500);
      if (onSubmitted) onSubmitted(data);
      // Auto-scroll to top so the success card is the first thing the
      // client sees (prevents "did anything happen?" confusion).
      requestAnimationFrame(() => {
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      });
    } catch (e) {
      setErrors({ _submit: e.message });
      setSubmitToast({ type: 'error', text: e.message || 'Submit failed — please try again.' });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setSubmitToast(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Locked state (server-resolved) ─────────────────────────────────
  // Read-only: the client sees everything they entered but cannot change or
  // submit it. Contact details come from the tenant's own company settings.
  if (isLockedByDate && !previewOnly) {
    const phone = (lockContact && lockContact.phone) || '';
    const email = (lockContact && lockContact.email) || '';
    return (
      <div className="space-y-4" data-testid="pf-locked">
        <div
          className="rounded-xl px-5 py-4 flex items-start gap-3"
          style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)' }}
        >
          <Lock className="w-5 h-5 text-[#D4AF37]/70 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-[#E7C75E]">Planning is closed for your event</p>
            <p className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
              Your planning details are locked in ahead of your event, so they can no
              longer be edited here. If you need to make a change, we're happy to help —
              please reach out and we'll take care of it.
            </p>
            {(phone || email) && (
              <div className="flex flex-wrap items-center gap-4 mt-3" data-testid="pf-locked-contact">
                {phone && (
                  <a href={`tel:${getPhoneDigits(phone)}`} className="inline-flex items-center gap-1.5 text-[12.5px] text-[#E7C75E] hover:underline" data-testid="pf-locked-phone">
                    <Phone className="w-3.5 h-3.5" /> {formatPhoneNumber(phone)}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-[12.5px] text-[#E7C75E] hover:underline" data-testid="pf-locked-email">
                    <Mail className="w-3.5 h-3.5" /> {email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <SchemaMirroredAnswers
          schema={{ ...schema, steps }}
          answers={answers}
          variant="client"
        />
      </div>
    );
  }

  // ── Submitted state ────────────────────────────────────────────────
  if (submitted && !editing) {
    return (
      <div className="space-y-4" data-testid="pf-submitted">
        {/* Keep the floating toast alive on the success view too */}
        {submitToast && (
          <div
            className="fixed inset-x-0 top-4 z-50 flex justify-center px-4 pointer-events-none"
            data-testid="pf-submit-toast"
          >
            <div
              className={`pointer-events-auto flex items-center gap-3 rounded-xl px-5 py-3 backdrop-blur-md shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300 ${
                submitToast.type === 'success'
                  ? 'bg-[#22C55E]/15 border border-[#22C55E]/40'
                  : 'bg-red-500/15 border border-red-500/40'
              }`}
            >
              {submitToast.type === 'success'
                ? <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                : <AlertCircle className="w-5 h-5 text-red-400" />}
              <span className={`text-[13px] font-medium ${
                submitToast.type === 'success' ? 'text-[#22C55E]' : 'text-red-300'
              }`}>
                {submitToast.text}
              </span>
            </div>
          </div>
        )}
        <div
          className="rounded-xl px-5 py-5 flex items-center gap-3"
          style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}
        >
          <CheckCircle2 className="w-6 h-6 text-[#22C55E]/60 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-[#22C55E]/80">Planning Form Submitted</p>
            <p className="text-[12px] text-white/30 mt-0.5">
              Thank you! Our team will review your details and begin preparing your event.
            </p>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-medium transition-all bg-[#D4AF37]/10 border border-[#D4AF37]/25 text-[#E7C75E] hover:bg-[#D4AF37]/15"
            data-testid="pf-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit Answers
          </button>
        </div>

        {/* ── What Happens Next — luxury reassurance strip (dismissible) ── */}
        <WhatHappensNext token={token} />

        {/* ── Read-only summary using shared SchemaMirroredAnswers ── */}
        <SchemaMirroredAnswers
          schema={{ ...schema, steps }}
          answers={answers}
          variant="client"
        />
      </div>
    );
  }

  if (!currentStep) return null;

  // ── Visible fields for current step ────────────────────────────────
  const visibleFields = (currentStep.fields || []).filter(f => isFieldVisible(f, answers));

  return (
    <div className="space-y-5 relative" data-testid="pf-renderer">
      {/* Inline submission toast — visible feedback when client hits Submit */}
      {submitToast && (
        <div
          className="fixed inset-x-0 top-4 z-50 flex justify-center px-4 pointer-events-none"
          data-testid="pf-submit-toast"
        >
          <div
            className={`pointer-events-auto flex items-center gap-3 rounded-xl px-5 py-3 backdrop-blur-md shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300 ${
              submitToast.type === 'success'
                ? 'bg-[#22C55E]/15 border border-[#22C55E]/40'
                : 'bg-red-500/15 border border-red-500/40'
            }`}
          >
            {submitToast.type === 'success'
              ? <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
              : <AlertCircle className="w-5 h-5 text-red-400" />}
            <span className={`text-[13px] font-medium ${
              submitToast.type === 'success' ? 'text-[#22C55E]' : 'text-red-300'
            }`}>
              {submitToast.text}
            </span>
          </div>
        </div>
      )}
      {previewOnly && (
        <p className="text-[12px] text-white/40" data-testid="pf-preview-note">
          Preview — nothing you do here is saved or sent.
        </p>
      )}
      {/* Step progress */}
      <StepProgress
        steps={steps}
        currentIdx={currentIdx}
        answers={answers}
        disabled={readOnly || (submitted && !editing)}
        onJump={(idx) => {
          if (idx === currentIdx) return;
          // Persist current progress without blocking the jump on validation —
          // client may want to skip ahead and come back to finish later.
          if (!readOnly && !(submitted && !editing)) {
            doSave(answers, steps[idx]?.id || currentStep.id);
          }
          setCurrentIdx(idx);
          setErrors({});
          // Intentionally NO scroll — the tab row sits near the top of the
          // form card, and auto-scrolling to top was disorienting when the
          // viewport was already there. Keep the viewport exactly where the
          // user clicked.
        }}
      />

      {/* Step header — luxury treatment */}
      <div className="pt-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
              {currentStep.title}
            </h3>
            {currentStep.description && (
              <p className="text-[14px] sm:text-[16px] text-white/45 mt-2 leading-snug">{currentStep.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-white/30 pt-1">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D4AF37]/50" />}
            {saveError && !saving && (
              <span className="flex items-center gap-1 text-red-400" data-testid="pf-save-error" title={saveError}>
                <AlertCircle className="w-3.5 h-3.5" />
                Not saved
              </span>
            )}
            {lastSaved && !saving && !saveError && (
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5 text-[#22C55E]/60" />
                Saved
              </span>
            )}
          </div>
        </div>

        {/* Completion bar — prominent gold gradient */}
        <div className="mt-6 flex items-center gap-4">
          <div className="flex-1 h-[7px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${completion.percent}%`,
                background: completion.percent === 100
                  ? 'linear-gradient(90deg, rgba(34,197,94,0.85), rgba(74,222,128,0.95))'
                  : 'linear-gradient(90deg, #9B7418 0%, #D4AF37 55%, #F2D989 100%)',
                boxShadow: completion.percent === 100
                  ? '0 0 18px rgba(34,197,94,0.25)'
                  : '0 0 20px rgba(212,175,55,0.28)',
              }}
            />
          </div>
          {(() => {
            const stepsLeft = Math.max(0, steps.length - 1 - currentIdx);
            const pct = completion.percent;
            // State-aware styling — cool silver at 0%, gold in progress,
            // emerald glow + check at 100% so "All set" reads unmistakably.
            let pillStyle;
            let pillColor;
            if (pct === 0) {
              pillStyle = {
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.14)',
                boxShadow: '0 0 0 rgba(255,255,255,0)',
              };
              pillColor = 'rgba(255,255,255,0.65)';
            } else if (pct === 100) {
              pillStyle = {
                background: 'rgba(34,197,94,0.1)',
                border: '1px solid rgba(74,222,128,0.35)',
                boxShadow: '0 0 22px rgba(34,197,94,0.28)',
              };
              pillColor = '#6EE7A6';
            } else {
              pillStyle = {
                background: 'rgba(212,175,55,0.08)',
                border: '1px solid rgba(212,175,55,0.22)',
                boxShadow: '0 0 18px rgba(212,175,55,0.1)',
              };
              pillColor = '#E6C86A';
            }
            return (
              <div
                className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs whitespace-nowrap font-medium transition-all duration-500"
                style={{ ...pillStyle, color: pillColor }}
                data-testid="pf-complete-pill"
              >
                {pct === 100 && <Check className="w-3 h-3" strokeWidth={3} />}
                {pct}% Complete{stepsLeft > 0 ? ` — ${stepsLeft} step${stepsLeft === 1 ? '' : 's'} left` : ' — All set'}
              </div>
            );
          })()}
          {adminSave && (songAdminEventId || booking?.eventId) && (
            <button
              type="button"
              onClick={() => { const id = songAdminEventId || booking?.eventId; if (id) window.location.assign(`/event/${id}?section=timeline&tlbuild=1`); }}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-all hover:-translate-y-px"
              style={{ background: 'linear-gradient(135deg,#F1D98A,#D4AF37)', color: '#0B0F1A' }}
              data-testid="pf-build-timeline"
            >
              Build timeline from planning →
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      <div className={currentStep.layout === 'paired' ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3' : 'space-y-4'}
        inert={previewOnly ? true : undefined}
        data-testid="pf-fields">
        {visibleFields.map(field => {
          if (field.type === 'song_picker') {
            return (
              <div
                key={field.id}
                className={currentStep.layout === 'paired' ? 'sm:col-span-2' : ''}
                data-testid={`pf-row-${field.id}`}
              >
                <label className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[11px] uppercase tracking-widest text-[#D4AF37]/70 font-medium">
                    {field.label}
                  </span>
                </label>
                {songAdminEventId ? (
                  <AdminSongSections eventId={songAdminEventId} />
                ) : (
                  <SongPickerField
                    songRequestLink={songRequestLink}
                    answers={answers}
                    onChange={handleChange}
                  />
                )}
              </div>
            );
          }
          if (field.type === 'repeater' || field.type === 'repeating_group') {
            return (
              <div
                key={field.id}
                className={currentStep.layout === 'paired' ? 'sm:col-span-2' : ''}
                data-testid={`pf-row-${field.id}`}
              >
                <RepeaterField
                  field={field}
                  value={answers[field.id]}
                  onChange={handleChange}
                />
              </div>
            );
          }
          return (
            <div
              key={field.id}
              className={field.fullWidth && currentStep.layout === 'paired' ? 'sm:col-span-2' : ''}
              data-testid={`pf-row-${field.id}`}
            >
              <label className="flex items-center gap-1.5 mb-1.5">
                <span className="text-[11px] uppercase tracking-widest text-[#D4AF37]/70 font-medium">
                  {field.label}
                </span>
                {field.required && <span className="text-[9px] text-[#D4AF37]/50">*</span>}
              </label>
              <FieldRenderer
                field={field}
                value={answers[field.id]}
                onChange={handleChange}
                error={errors[field.id]}
              />
              {prefilledFields.has(field.id) && (
                <p className="text-[10px] text-[#D4AF37]/50 mt-1">Filled in from your booking — update if it changed.</p>
              )}
              {errors[field.id] && (
                <p className="text-[10px] text-red-400/70 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors[field.id]}
                </p>
              )}
              {field.helpText && (
                <p className="text-[10px] text-white/15 mt-1">{field.helpText}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit error */}
      {errors._submit && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-3.5 h-3.5 text-red-400" />
          <span className="text-[12px] text-red-400">{errors._submit}</span>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <button
          onClick={handleBack}
          disabled={currentIdx === 0}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2.5 min-h-[44px] sm:min-h-0 text-[12px] font-medium transition-all ${
            currentIdx === 0
              ? 'text-white/15 cursor-not-allowed'
              : 'text-white/40 hover:text-white/60 hover:bg-white/[0.04]'
          }`}
          data-testid="pf-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <div className="flex items-center gap-2">
          {/* Manual save */}
          <button
            onClick={() => doSave(answers, currentStep.id)}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-full px-3 py-2 min-h-[44px] sm:min-h-0 text-[11px] font-medium text-white/25 hover:text-white/45 transition"
            data-testid="pf-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>

          {isLastStep ? (
            adminSave ? (
              <button
                onClick={() => doSave(answersRef.current, currentStep.id)}
                disabled={saving}
                className="flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-wide transition-all duration-300 hover:-translate-y-px disabled:opacity-40 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#E7C75E]"
                data-testid="pf-admin-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-wide transition-all duration-300 hover:-translate-y-px disabled:opacity-40"
              style={{
                background: 'linear-gradient(135deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.08) 100%)',
                border: '1px solid rgba(34,197,94,0.25)',
                color: '#22C55E',
                boxShadow: '0 2px 16px rgba(34,197,94,0.08)',
              }}
              data-testid="pf-submit-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {submitting ? 'Submitting...' : "I'm done — submit my planning"}
            </button>
            )
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-1.5 rounded-full px-5 py-2.5 min-h-[44px] sm:min-h-0 text-[13px] font-medium transition-all duration-300 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#E7C75E] hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(212,175,55,0.15)]"
              data-testid="pf-next-btn"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────
   WhatHappensNext — dismissible reassurance strip.
   Shown to the client right after planning submission;
   persists dismissal per token so it doesn't keep
   reappearing on subsequent portal visits.
   ──────────────────────────────────────────────────── */
function WhatHappensNext({ token }) {
  const storageKey = token ? `mtl.whn.dismissed.${token}` : 'mtl.whn.dismissed.global';
  const [dismissed, setDismissed] = React.useState(() => {
    try { return appStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  if (dismissed) return null;

  const handleDismiss = () => {
    try { appStorage.setItem(storageKey, '1'); } catch {}
    setDismissed(true);
  };

  const cards = [
    { icon: Eye, title: "We're reviewing your details", body: "Our team is going through every answer to make sure your event is fully understood." },
    { icon: CalendarCheck, title: "We'll confirm within 24–48 hours", body: "Expect a confirmation with any clarifying questions or adjustments to the plan." },
    { icon: MailCheck, title: "Final timeline emailed to you", body: "Once locked, you'll receive your complete run-of-show timeline for the big day." },
  ];

  return (
    <div className="pt-2 relative print-hide" data-testid="pf-whats-next">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]/70 font-medium">
          What Happens Next
        </div>
        <button
          onClick={handleDismiss}
          className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] text-white/35 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
          data-testid="pf-whats-next-dismiss"
          aria-label="Dismiss What Happens Next"
        >
          Got it
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={i}
              className="rounded-[20px] p-4 transition-all hover:-translate-y-[1px]"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
                border: '1px solid rgba(212,175,55,0.15)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.025), 0 0 22px rgba(212,175,55,0.04)',
              }}
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, rgba(212,175,55,0.18), rgba(212,175,55,0.04))',
                    border: '1px solid rgba(212,175,55,0.22)',
                  }}>
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="text-[10px] text-[#D4AF37]/60 tracking-[0.2em] uppercase font-medium">Step {i + 1}</div>
              </div>
              <h4 className="text-[14px] font-semibold text-white leading-snug mb-1.5">{step.title}</h4>
              <p className="text-[12px] text-white/55 leading-relaxed">{step.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
